import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { actions as formActions, Control, LocalForm, Errors } from 'react-redux-form';
import ValidationErrors from '../forms/ValidationErrors';
import ModalComponent from '../ModalComponent';
import {
  fields as fieldsDuck,
  contractFees,
  customers as customersDuck,
  flashMessages,
  modals
} from '../../ducks';
import ControlGroup from '../forms/ControlGroup';
import './styles/ContractFeeModal.scss';
import { duckify, filterDuck, singleDuck } from '../../utils/duckify';
import { getCurrencySymbol } from '../../utils/i18n';

let formDispatch;
const isNumber = value => !isNaN(parseFloat(value)) && isFinite(value);

const ContractFeeModal = ({
  onClose,
  onSave,
  onUnexpectedError,
  billingCategories,
  contractFee,
  amountTypes,
  reloadContractFees,
  showFlash,
  customer,
  allFields
}) => {
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentFeeFields, setFeeFields] = useState([]);

  let initialContractFee = contractFee;

  if (initialContractFee !== undefined) {
    const normalizedChargeType = initialContractFee.charge_type || 'deposit_fee';
    const normalizedAmountType = initialContractFee.amount_type || 'fixed';
    const normalizedBillingCategoryId = initialContractFee.billing_category_id || billingCategories[0].id;
    const defaultPercentOf = initialContractFee.percent_of || [];

    initialContractFee = Object.assign({}, initialContractFee, {
      charge_type: normalizedChargeType,
      amount_type: normalizedAmountType,
      billing_category_id: normalizedBillingCategoryId,
      percent_of: defaultPercentOf
    });
  }

  const [currentContractFee, setContractFee] = useState(initialContractFee);
  const availableChargeTypes = [
    ['Deposit Fee', 'deposit_fee'],
    ['Optional Fee', 'optional'],
    ['Payment Plan Fee', 'payment_plan'],
    ['Required Fee', 'required'],
    ['Tuition', 'tuition'],
    ['Tuition Discount', 'tuition_discount']
  ];

  const feeOrdering = {
    tuition: 0,
    required: 1,
    optional: 2,
    tuition_discount: 3,
    deposit_fee: 4,
    payment_plan: 5
  };

  const optionsForPercentOf = [
    ['Tuition', 'tuition'],
    ['Required Fees', 'required'],
    ['Financial Aid Amount', 'financial_aid_amount'],
    ['Scholarship Amount', 'scholarship_amount'],
    ['Tuition Discounts', 'tuition_discount'],
    ['Deposit', 'deposit_fee']
  ];

  const feeMultiplier = {
    tuition_discount: -1,
    financial_aid_amount: -1,
    scholarship_amount: -1,
    deposit_fee: -1
  };

  const fetchChargeTypeIndex = () => {
    if (currentContractFee.charge_type === undefined) {
      return -1;
    }

    return (feeOrdering[currentContractFee.charge_type] || -1);
  };

  const fetchPercentOptionIndex = (percentOptionValue) => (feeOrdering[percentOptionValue] || -1);

  const fetchFilteredPercentOptions = (chargeTypeIndex) => (
    optionsForPercentOf.filter((optionItem) => (fetchPercentOptionIndex(optionItem[1]) < chargeTypeIndex))
  );

  const addChargeFeeMultiplier = (value) => {
    const selectedMultiplier = feeMultiplier[value];
    if (selectedMultiplier === undefined || selectedMultiplier >= 0) {
      return '';
    }

    return '(-)';
  };

  const normalizedForAmount = () => {
    let precision = 2;
    if (currentContractFee.amount_type === 'percent') {
      precision = 6;
    }

    let currentAmount = currentContractFee.amount !== undefined ? currentContractFee.amount : 0;
    currentAmount = parseFloat(currentAmount).toFixed(precision);

    formDispatch(formActions.merge('contract_fee', {
      contract_fee: {
        amount: currentAmount
      }
    }));
  };

  const filteredChargedTypes = () => {
    if (currentContractFee.amount_type === 'fixed') {
      return availableChargeTypes;
    }

    return availableChargeTypes.filter((item) => (item[1] !== 'tuition'));
  };

  const filteredFields = () => {
    const selectedFieldCtypes = ['ScholarshipField', 'RequiredFeeField', 'OptionalFeeField'];
    const selectedFieldNames = ['scholarship_amount', 'financial_aid_amount'];

    return allFields.filter((item) => selectedFieldCtypes.includes(item.ctype) || selectedFieldNames.includes(item.name));
  };

  useEffect(() => {
    setFeeFields(filteredFields());
  }, [allFields]);

  useEffect(() => {
    normalizedForAmount();
  }, []);

  const showPercentOfInput = () => {
    const chargeTypeIndex = fetchChargeTypeIndex();
    if (chargeTypeIndex === -1) {
      return '';
    }

    const filteredPercentOptions = fetchFilteredPercentOptions(chargeTypeIndex);
    if (filteredPercentOptions.length === 0) {
      return '';
    }

    const optionItems = filteredPercentOptions.map((option) => (
      <div className='contract-fee-percent-item' key={option[1]}>
        <label key={option[1]} className='checkbox'>
          <input
            type='checkbox'
            name='.contract_fee.percent_of[]'
            className='form-check-inline'
            checked={currentContractFee.percent_of !== undefined && currentContractFee.percent_of.includes(option[1])}
            onChange={(event) => {
              let newPercentOf = currentContractFee.percent_of.slice();
              newPercentOf.push(option[1]);

              if (event.target.checked === false) {
                newPercentOf = newPercentOf.filter((item) => (item !== option[1]));
              }

              formDispatch(formActions.merge('contract_fee', {
                contract_fee: {
                  percent_of: newPercentOf
                }
              }));
            }}
          />

          <span>
            {option[0]}
            {' '}
            {addChargeFeeMultiplier(option[1])}
          </span>
        </label>
      </div>

    ));

    return (
      <ControlGroup label='Percent of' key='percent_of' errors={errors.percent_of} showErrors>
        <div className='checkbox-list'>
          {optionItems}
        </div>
      </ControlGroup>
    );
  };

  const showAmountInput = () => {
    const amountSign = currentContractFee.amount_type === 'percent' ? '%' : getCurrencySymbol();

    return (
      <ControlGroup label='Amount' key='amount' errors={errors.amount} showErrors>
        <div className='input-group'>
          <Control.text
            model='.contract_fee.amount'
            placeholder='0.00'
            className='form-control form-control-sm'
            type='number'
            controlProps={{ step: '0.01' }}
            onBlur={normalizedForAmount}
            validators={{ isNumber }}
          />
          <span className='input-group-text'>{amountSign}</span>
        </div>
        <Errors
          className='text-danger'
          model='.contract_fee.amount'
          show='touched'
          messages={{
            isNumber: 'The value should be a valid number.'
          }}
        />
      </ControlGroup>
    );
  };

  const showFieldsInput = () => (
    <ControlGroup label='Field' key='field' errors={errors.field} showErrors>
      <Control.select className='form-control form-control-sm w-100' model='.contract_fee.field_id'>
        <option key='empty' value='' />
        {
          currentFeeFields.map(feeField => (
            <option key={feeField.id} value={feeField.id}>
              {feeField.display_name}
            </option>
          ))
        }
      </Control.select>
    </ControlGroup>
  );

  const showSuccessMessage = (contractFeeObject) => {
    let message;
    if (contractFeeObject.id) {
      message = `Contract Fee "${contractFeeObject.name}" has been edited.`;
    } else {
      message = `Contract Fee "${contractFeeObject.name}" has been created.`;
    }

    showFlash({ message });
  };

  const initialState = {
    contract_fee: initialContractFee
  };

  const selectedFeeField = () => {
    if (currentContractFee.field_id === undefined || currentFeeFields === undefined) {
      return undefined;
    }

    return currentFeeFields.find((feeField) => currentContractFee.field_id === feeField.id);
  };

  const isDepositValid = () => (
    ['payment_plan', 'optional', 'required'].includes(currentContractFee.charge_type)
    || (selectedFeeField() !== undefined
      && ['OptionalFeeField', 'RequiredFeeField'].includes(selectedFeeField().ctype))
  );

  // handle charge_type changes
  useEffect(() => {
    if (currentContractFee.charge_type === 'deposit_fee') {
      formDispatch(formActions.merge('contract_fee', {
        contract_fee: {
          payer_specific_amounts: false
        }
      }));
    }

    const chargeTypeIndex = fetchChargeTypeIndex();

    if (chargeTypeIndex === -1) {
      formDispatch(formActions.merge('contract_fee', {
        contract_fee: {
          percent_of: []
        }
      }));
    }

    if (chargeTypeIndex !== -1 && currentContractFee.percent_of !== undefined) {
      const filteredPercentOptions = fetchFilteredPercentOptions(chargeTypeIndex);
      const filteredValues = filteredPercentOptions.map((item) => item[1]);
      let newPercentOptions = currentContractFee.percent_of.slice();

      if (filteredPercentOptions.length === 0) {
        formDispatch(formActions.merge('contract_fee', {
          contract_fee: {
            percent_of: []
          }
        }));
      } else {
        currentContractFee.percent_of.forEach((percentOfItem) => {
          if (filteredValues.includes(percentOfItem) === false) {
            newPercentOptions = newPercentOptions.filter((item) => item !== percentOfItem);
          }
        });

        if (currentContractFee.percent_of.slice().sort() !== newPercentOptions.slice().sort()) {
          formDispatch(formActions.merge('contract_fee', {
            contract_fee: {
              percent_of: newPercentOptions
            }
          }));
        }
      }
    }
  }, [currentContractFee.charge_type]);

  // handle amount_type changes
  useEffect(() => {
    const updatedValues = {};

    if (currentContractFee.amount_type === 'percent' && currentContractFee.charge_type === 'tuition') {
      updatedValues.charge_type = '';
    }

    if (currentContractFee.amount_type === 'field') {
      updatedValues.amount = '';
      updatedValues.charge_type = '';
    } else {
      updatedValues.field_id = '';
    }

    formDispatch(formActions.merge('contract_fee', {
      contract_fee: updatedValues
    }));
  }, [currentContractFee.amount_type]);

  const isPayerSpecificAmountsShown = () => (
    customer.split_contracts_enabled && currentContractFee.charge_type !== 'deposit_fee'
  );

  return (
    <LocalForm
      model='contract_fee'
      initialState={initialState}
      onChange={values => {
        setContractFee(values.contract_fee);
      }}
      onSubmit={
        values => {
          setLoading(true);

          onSave(values.contract_fee)
            .then(onClose)
            .then(reloadContractFees)
            .then(() => showSuccessMessage(values.contract_fee))
            .catch(response => {
              setLoading(false);
              if (response.contract_fee && response.contract_fee.errors) {
                setErrors(response.contract_fee.errors);
              } else {
                onClose();
                onUnexpectedError();
              }
            });
        }
      }
      getDispatch={(dispatch) => {
        formDispatch = dispatch;
      }}
    >
      {(formProps) => (
        <ModalComponent
          title={contractFee.id ? 'Edit Contract Fee Details' : 'Add Contract Fee Details'}
          buttons={[
            <input
              className='btn btn-primary'
              type='submit'
              disabled={loading || formProps.invalid}
              value={loading ? 'Saving...' : 'Save'}
              onClick={() => formDispatch(formActions.submit('contract_fee'))}
            />
          ]}
        >
          <div className='form-horizontal'>
            <ValidationErrors errors={errors} />

            <ControlGroup label='Name' key='name' errors={errors.name} showErrors>
              <Control.text model='.contract_fee.name' placeholder='e.g. Contract Fee Name' className='w-100' />
            </ControlGroup>

            <ControlGroup label='Canonical Name' key='canonical_name' errors={errors.canonical_name} showErrors>
              <Control.text
                model='.contract_fee.canonical_name'
                placeholder='e.g. Canonical Name'
                disabled={contractFee.id !== undefined}
                className='w-100'
              />
            </ControlGroup>

            <ControlGroup label='Billing Category' key='category' errors={errors.category_id} showErrors>
              <Control.select className='form-control form-control-sm w-100' model='.contract_fee.billing_category_id'>
                {
                  billingCategories.map((group) => (
                    <optgroup key={group.id} label={group.name}>
                      <option value={group.id}>{group.name}</option>
                      {
                        group.children.filter(c => c.active).map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))
                      }
                    </optgroup>
                  ))
                }
              </Control.select>
            </ControlGroup>

            <ControlGroup label='Amount Type' key='amount_type' errors={errors.amount_type} showErrors>
              <Control.select
                className='form-control form-control-sm w-100'
                model='.contract_fee.amount_type'
              >
                {
                  amountTypes.map((amountType) => (
                    <option value={amountType.id} key={amountType.id}>{amountType.name}</option>
                  ))
                }
              </Control.select>
            </ControlGroup>

            {currentContractFee.amount_type !== 'field' && (
              <ControlGroup label='Charge Type' key='charge_type' errors={errors.charge_type} showErrors>
                <Control.select
                  className='form-control form-control-sm w-100'
                  model='.contract_fee.charge_type'
                >
                  <option key='empty-value' value='' />
                  {
                    filteredChargedTypes().map((chargeTypeItem) => (
                      <option key={chargeTypeItem[1]} value={chargeTypeItem[1]}>
                        {chargeTypeItem[0]}
                      </option>
                    ))
                  }
                </Control.select>
              </ControlGroup>
            )}

            {currentContractFee.amount_type !== 'field' && showAmountInput()}
            {currentContractFee.amount_type === 'percent' && showPercentOfInput()}
            {currentContractFee.amount_type === 'field' && showFieldsInput()}

            <ControlGroup label='Explanation' key='description' errors={errors.description} showErrors>
              <Control.textarea
                model='.contract_fee.description'
                placeholder='Enter explanation here'
                className='w-100 contract-explanation-textarea'
              />
            </ControlGroup>

            {isPayerSpecificAmountsShown() && (
              <ControlGroup
                label='Allow Payer-Specific Overrides'
                key='payer_specific_amounts'
                errors={errors.payer_specific_amounts}
                showErrors
              >
                <Control.checkbox model='.contract_fee.payer_specific_amounts' className='form-check-inline' />
              </ControlGroup>
            )}

            {isDepositValid() && (
              <ControlGroup label='Add To Deposit?' key='add_to_deposit' errors={errors.add_to_deposit} showErrors>
                <Control.checkbox model='.contract_fee.add_to_deposit' className='form-check-inline' />
              </ControlGroup>
            )}
          </div>
        </ModalComponent>
      )}
    </LocalForm>
  );
}

ContractFeeModal.propTypes = {
  /** Billing categories a charge/credit can be added to */
  billingCategories: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),

  amountTypes: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),

  contractFee: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    canonical_name: PropTypes.string,
    amount: PropTypes.string,
    billing_category_name: PropTypes.string,
    active: PropTypes.bool,
    last_edit: PropTypes.string,
    description: PropTypes.string,
    amount_type: PropTypes.string,
    field_id: PropTypes.string,
    fee_fields: PropTypes.array,
    payer_specific_amounts: PropTypes.bool,
    percent_of: PropTypes.array
  }),
  allFields: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    display_name: PropTypes.string,
    ctype: PropTypes.string
  })),

  showFlash: PropTypes.func
};

const mapDucksToProps = () => ({
  customer: singleDuck(customersDuck, 'current'),
  allFields: filterDuck(fieldsDuck)
});

const mapDispatchToProps = (dispatch) => ({
  reloadContractFees: () => dispatch(contractFees.actions.markDirty()),
  onSave: (contractFee) => {
    if (contractFee.id !== undefined) {
      return dispatch(contractFees.actions.update(contractFee, { id: contractFee.id }));
    }
    return dispatch(contractFees.actions.create(contractFee));
  },
  onClose: () => dispatch(modals.actions.hide()),
  onUnexpectedError: () => dispatch(modals.actions.show('UnexpectedError')),
  showFlash: (props) => dispatch(flashMessages.actions.show(props))
});

ContractFeeModal.defaultProps = {
  amountTypes: [
    { id: 'field', name: 'Field' },
    { id: 'percent', name: 'Percentage' },
    { id: 'fixed', name: 'Fixed' }
  ],
  contractFee: undefined,
  showFlash: () => false,
  allFields: []
};

export default duckify(mapDucksToProps, () => {}, mapDispatchToProps)(ContractFeeModal);
